// Rich per-service copy for service detail pages, keyed by service slug.
// Kept separate from the navigation/content model for maintainability.

export const SERVICE_DETAILS = {
  // ---------- People & HR ----------
  recruitment: {
    summary: "End-to-end recruitment that fills roles with the right people — not just any people.",
    overview:
      "We run the full hiring cycle for you: understanding the role in context, engaging the market, and shortlisting candidates who fit the work and the team. The goal is a deliberate hire, not a rushed one.",
    forWho: "Organisations hiring for specific roles that matter.",
    included: [
      "Role scoping and success-profile definition",
      "Targeted candidate outreach and screening",
      "Structured shortlists with clear rationale",
      "Interview coordination and offer support",
    ],
    outcomes: ["Stronger shortlists in less time", "Fewer mis-hires", "A hiring process you can trust"],
  },
  "candidate-sourcing": {
    summary: "Proactive sourcing that surfaces qualified, hard-to-find talent.",
    overview:
      "When the right people aren't applying, we go and find them. We build targeted pipelines of passive and active candidates mapped to your specific requirements.",
    forWho: "Teams with niche or competitive roles.",
    included: [
      "Market and talent mapping",
      "Boolean and multi-channel sourcing",
      "Initial engagement and interest gauging",
      "Qualified pipeline handover",
    ],
    outcomes: ["Access to passive talent", "A warm, qualified pipeline", "Reduced time-to-shortlist"],
  },
  rpo: {
    summary: "Recruitment Process Outsourcing — hiring capacity that scales with you.",
    overview:
      "We embed as an extension of your team to own part or all of your recruitment function, giving you structure and throughput without permanent headcount.",
    forWho: "Companies scaling hiring without adding fixed overhead.",
    included: [
      "Dedicated recruitment capacity",
      "Process design and workflow setup",
      "Employer-brand-aligned candidate experience",
      "Reporting and hiring analytics",
    ],
    outcomes: ["Predictable hiring throughput", "Lower cost per hire", "A repeatable process"],
  },
  "hr-consulting": {
    summary: "Practical HR consulting that fits how your organisation really works.",
    overview:
      "From policies to people processes, we help you put the right foundations in place — pragmatic, compliant, and built for where your business is now.",
    forWho: "Growing organisations formalising their HR.",
    included: [
      "HR audit and gap assessment",
      "Policy and process design",
      "Onboarding and lifecycle frameworks",
      "Advisory on day-to-day people issues",
    ],
    outcomes: ["Clear, usable HR foundations", "Reduced people risk", "More time for leadership"],
  },
  "workforce-planning": {
    summary: "Align your people plan with where the business is going.",
    overview:
      "We help you anticipate the roles, skills and capacity you'll need — so hiring becomes proactive and intentional rather than reactive.",
    forWho: "Leaders planning growth or restructuring.",
    included: [
      "Current-state capability review",
      "Demand and skills forecasting",
      "Org-structure and role planning",
      "Phased hiring roadmap",
    ],
    outcomes: ["A clear people roadmap", "Right skills at the right time", "Fewer capacity surprises"],
  },
  "talent-strategy": {
    summary: "A deliberate strategy to attract, grow and keep the right people.",
    overview:
      "We connect your talent decisions to your business objectives — covering attraction, development and retention as one coherent strategy.",
    forWho: "Organisations treating talent as a strategic advantage.",
    included: [
      "Talent objective alignment",
      "Attraction and employer-brand direction",
      "Retention and development levers",
      "Measurable strategy roadmap",
    ],
    outcomes: ["Talent aligned to strategy", "Stronger retention", "A competitive people edge"],
  },

  // ---------- Career ----------
  "resume-building": {
    summary: "A resume that tells your real story with clarity and impact.",
    overview:
      "We craft a resume built around your strengths and target roles — clear, credible, and easy for both recruiters and systems to read.",
    forWho: "Professionals at any stage who need a stronger resume.",
    included: [
      "Discovery of your experience and goals",
      "Achievement-focused writing",
      "Clean, ATS-friendly formatting",
      "Tailoring guidance for applications",
    ],
    outcomes: ["A resume you're confident to send", "Clearer positioning", "More interview callbacks"],
  },
  "resume-optimisation-ats-review": {
    summary: "Make sure your resume passes the ATS and reaches human eyes.",
    overview:
      "We review your existing resume against applicant tracking systems and recruiter expectations, then optimise structure, keywords and clarity.",
    forWho: "Applicants not hearing back despite applying.",
    included: [
      "ATS compatibility review",
      "Keyword and relevance alignment",
      "Structure and readability fixes",
      "Actionable improvement notes",
    ],
    outcomes: ["Higher ATS pass-through", "Better recruiter response", "A sharper resume"],
  },
  "linkedin-optimisation": {
    summary: "Turn your LinkedIn profile into an opportunity magnet.",
    overview:
      "We optimise your headline, About section, experience and keywords so the right recruiters and opportunities find you.",
    forWho: "Professionals wanting to be discovered.",
    included: [
      "Headline and About rewrite",
      "Experience and skills optimisation",
      "Keyword and search visibility tuning",
      "Profile presentation guidance",
    ],
    outcomes: ["More profile visibility", "Inbound opportunities", "A consistent professional story"],
  },
  "interview-preparation": {
    summary: "Walk into interviews prepared, calm and convincing.",
    overview:
      "We prepare you for the interviews that matter — structuring your stories, practising responses, and building genuine confidence.",
    forWho: "Anyone with interviews on the horizon.",
    included: [
      "Role and company preparation",
      "Behavioural and technical practice",
      "Mock interviews with feedback",
      "Answer frameworks and follow-up strategy",
    ],
    outcomes: ["More confidence", "Clearer, stronger answers", "Better conversion to offers"],
  },
  "career-consultation": {
    summary: "One-to-one guidance to make your next move deliberate.",
    overview:
      "A focused conversation about where you are and where you want to go — with honest, practical direction on how to get there.",
    forWho: "Professionals at a crossroads or planning a move.",
    included: [
      "Career-goal clarification",
      "Options and trade-off analysis",
      "Personalised action plan",
      "Accountability next steps",
    ],
    outcomes: ["Clarity on direction", "A concrete plan", "Confidence in your next move"],
  },
  "professional-branding": {
    summary: "A consistent, credible professional brand across every touchpoint.",
    overview:
      "We help you define and express what makes you distinct — so your resume, LinkedIn and presence tell one coherent, compelling story.",
    forWho: "Professionals and leaders raising their profile.",
    included: [
      "Personal positioning and narrative",
      "Consistent messaging across channels",
      "Bio and profile copy",
      "Presence and visibility guidance",
    ],
    outcomes: ["A clear personal brand", "Consistent messaging", "Stronger professional presence"],
  },
  "job-search-strategy": {
    summary: "A structured search plan so effort turns into offers.",
    overview:
      "We replace scattered applying with a targeted strategy — the right roles, the right channels, and a system to manage it all.",
    forWho: "Active job seekers who want results, not busywork.",
    included: [
      "Target role and market definition",
      "Channel and networking strategy",
      "Application tracking system",
      "Weekly focus and cadence",
    ],
    outcomes: ["A focused search", "Better-quality applications", "Momentum toward offers"],
  },
  "cover-letter-application-support": {
    summary: "Tailored applications that make each submission count.",
    overview:
      "We help you craft compelling cover letters and complete applications that connect your story directly to what each role needs.",
    forWho: "Applicants targeting specific roles.",
    included: [
      "Role-specific cover letters",
      "Application review and tailoring",
      "Messaging and tone alignment",
      "Submission best-practice guidance",
    ],
    outcomes: ["Stronger applications", "Better response rates", "Less application stress"],
  },

  // ---------- AI & Digital ----------
  "business-website-development": {
    summary: "A modern, credible website that works as hard as your business.",
    overview:
      "We design and build clean, responsive business websites that load fast, read clearly, and turn visitors into enquiries — built to grow with you.",
    forWho: "Businesses that need a credible online presence.",
    included: [
      "Structure and content planning",
      "Responsive, modern design",
      "Development and launch",
      "Foundations for future features",
    ],
    outcomes: ["A professional online presence", "A fast, responsive site", "A base you can build on"],
  },
  "ai-powered-website-development": {
    summary: "Websites accelerated with AI — built faster, built smart.",
    overview:
      "We use AI to accelerate design and build while keeping human judgement in the loop — so you get a modern, capable website sooner, without cutting corners.",
    forWho: "Businesses wanting speed and modern capability.",
    included: [
      "AI-accelerated design and content",
      "Modern, responsive build",
      "Human review and quality control",
      "Room for AI-driven features later",
    ],
    outcomes: ["Faster time to launch", "A modern, capable site", "Future-ready foundations"],
  },
};
